<script setup lang="ts">
import { computed, type PropType } from 'vue'
import type { CabinetMeterType } from '../model/types'
import { VIcons } from '@/shared/ui'
import { useRoute } from 'vue-router'
import { useSendControlMeterCommand } from '../model/api'
import type { MeterLimitsType } from '@/entities/Meters'
import { useAuth } from '@/entities/Auth'

const props = defineProps({
  meter: {
    type: Object as PropType<CabinetMeterType>,
    required: true,
  },
  limits: {
    type: Object as PropType<MeterLimitsType | null>,
    required: true,
  },
  contactorStatus: {
    type: Boolean,
    default: true,
  },
  isMonitoring: {
    type: Boolean,
    default: true,
  },
  isSummaryLimits: {
    type: Boolean,
    default: true,
  },
  objectId: {
    type: Number,
    required: false,
  },
})

const route = useRoute()
const { send, isPending } = useSendControlMeterCommand()
const objectId = computed(() => props.objectId || route.params.object_id)
const { isPermissions } = useAuth()

/**
 * Функция вызова получения мгновенных данных по счетчику
 */
const meterReload = async (type: string) => {
  await send(props.meter.order_number, type, Number(objectId.value))
}

/* Вычисляем размер сетки */
const gridStyle = computed(() => ({
  'grid-template-columns': `repeat(${props.meter.tariff_count + 2}, 1fr)`,
}))

/* Сетка значений */
const matrixCurrent = {
  a: ['u', 'i', 'p', 'q'],
  b: ['u', 'i', 'p', 'q'],
  c: ['u', 'i', 'p', 'q'],
}

/**
 * Функция вычисления статуса по питанию
 * @param key
 * @param value
 */
const getStatusClass = (key: string, value: number) => {
  const isSummaryP = props.isSummaryLimits && key.includes('p')

  if (!props.limits || !props.isMonitoring || isSummaryP) return 'w-cabinet-meter__tab-data_success'

  const absValue = Math.abs(value)
  const { limits, contactorStatus } = props
  const maxLimit = limits[`limit_max_${key}`]
  const minLimit = limits[`limit_min_${key}`]
  const threshold = limits[`threshold_${key}`]

  const isOutOfLimits = absValue > maxLimit || absValue < minLimit

  if (contactorStatus) {
    return isOutOfLimits ? 'w-cabinet-meter__tab-data_error' : 'w-cabinet-meter__tab-data_success'
  } else {
    return !isOutOfLimits || absValue <= threshold
      ? 'w-cabinet-meter__tab-data_success'
      : 'w-cabinet-meter__tab-data_error'
  }
}
</script>

<template>
  <div class="w-cabinet-meter__current">
    <div class="w-cabinet-meter__row_current">
      <div
        class="w-cabinet-meter__reload"
        :class="{ disabled: isPending || !isPermissions(['DEVICE_CONTROL']) }"
      >
        <VIcons type="cached" @click="meterReload('instant')" />
      </div>

      <div class="w-cabinet-meter__tab-head">U</div>
      <div class="w-cabinet-meter__tab-head">I</div>
      <div class="w-cabinet-meter__tab-head">P</div>
      <div class="w-cabinet-meter__tab-head">Q</div>
    </div>

    <div class="w-cabinet-meter__row_current" v-for="(item, key) in matrixCurrent">
      <div class="w-cabinet-meter__tab-head">
        {{ key.toUpperCase() }}
      </div>

      <div
        class="w-cabinet-meter__tab-data"
        :class="getStatusClass(`${type}_${key}`, meter.current[`${type}_${key}`] || 0)"
        v-for="type in item"
      >
        {{ Math.abs(meter.current[`${type}_${key}`]) || 0 }}
      </div>
    </div>
  </div>

  <div class="w-cabinet-meter__statement">
    <div class="w-cabinet-meter__row_statement" :style="gridStyle">
      <div
        class="w-cabinet-meter__reload"
        :class="{ disabled: isPending || !isPermissions(['DEVICE_CONTROL']) }"
      >
        <VIcons type="cached" @click="meterReload('statement')" />
      </div>

      <div class="w-cabinet-meter__tab-head" :title="$t('itf.sum_value')">∑</div>

      <div
        v-for="item in meter.tariff_count"
        class="w-cabinet-meter__tab-head"
        :title="$t('itf.tariff_count', { count: item })"
      >
        T{{ item }}
      </div>
    </div>

    <div class="w-cabinet-meter__row_statement" :style="gridStyle" v-if="meter.statement.a">
      <div class="w-cabinet-meter__tab-head" :title="$t('itf.energy_active')">A</div>

      <div
        v-for="(item, index) in meter.statement.a"
        class="w-cabinet-meter__tab-data"
        :title="
          index ? $t('itf.energy_active_count', { count: index }) : $t('itf.energy_active_sum')
        "
      >
        {{ Math.round(item / 1000) || 0 }}
      </div>
    </div>

    <div class="w-cabinet-meter__row_statement" :style="gridStyle" v-if="meter.statement.r">
      <div class="w-cabinet-meter__tab-head" :title="$t('itf.energy_reactive')">R</div>

      <div
        v-for="(item, index) in meter.statement.r"
        class="w-cabinet-meter__tab-data"
        :title="
          index ? $t('itf.energy_reactive_count', { count: index }) : $t('itf.energy_reactive_sum')
        "
      >
        {{ Math.round(item / 1000) || 0 }}
      </div>
    </div>
  </div>
</template>
